import React from 'react';
import { observer } from 'mobx-react';
import { TabsWrapper, Tab, Content, SubTab, SubTabsWrapper } from '@draftkings/dk-tab-switcher';
import { IState } from '../../contracts';
import { Section } from '../Section/Section';
import { SBMessageBus, TrackEventFunction } from '@draftkings/sportsbook-common-contracts';
import type { LocalizationConfig } from '@draftkings/sportsbook-labels';
import { TeamLogoProps } from '@draftkings/scoreboards';
import { Marketboard } from '../Marketboard';
import { LivePageError } from '../LivePageError';
import { dict } from '../../helpers';

import '@draftkings/scoreboards/style.css';
import '@draftkings/component-builder/style.css';
import '@draftkings/dk-tab-switcher/style.css';
import '../../variables.css';
import './index.scss';

type AppProps = {
    consumerVersion: string;
    dataProvider: IState;
    trackEvent: TrackEventFunction;
    getTeamLogo: (props: TeamLogoProps) => JSX.Element;
    messageBus: SBMessageBus;
    localizationConfig: LocalizationConfig;
    locale: string;
};

class AppComponent extends React.Component<AppProps, IState> {
    private marketboardRef: React.RefObject<HTMLDivElement>;
    private localizationConfig: LocalizationConfig;
    constructor(props: AppProps) {
        super(props);
        this.marketboardRef = React.createRef<HTMLDivElement>();
        this.localizationConfig = props.localizationConfig;
    }

    componentDidMount() {
        this.props.messageBus.on('on_promotion_data_change', this.onPromotionDataChange, this);
    }

    componentWillUnmount() {
        this.props.messageBus.off('on_promotion_data_change', this.onPromotionDataChange);
    }

    onPromotionDataChange() {
        this.forceUpdate();
    }

    render() {
        const baseClass = 'lp-live-page-widget';
        if (this.props.dataProvider.tabs.length === 0 && this.props.dataProvider.isError) {
            return (
                <section className={baseClass}>
                    <div className="lp-page-widget__error" data-testid="live-page-widget-error" role="status">
                        {this.props.localizationConfig.livePage.noLiveGamesMessage}
                    </div>
                </section>
            );
        }

        return (
            <>
                <section
                    className={baseClass}
                    aria-label={
                        this.props.localizationConfig?.livePage?.liveEventsAriaLabel || dict.liveEventsAriaLabel
                    }
                >
                    <TabsWrapper dictionary={this.props.localizationConfig?.tabSwitcher}>
                        {this.props.dataProvider.tabs.map((tab) => (
                            <Tab
                                route={tab.id}
                                key={tab.id}
                                controller={this.props.dataProvider.tabSwitcherController}
                                isSelectedByDefault={tab.isSelected}
                                onClick={() =>
                                    this.props.dataProvider.onTabClick(tab.id, tab.associatedData.seoIdentifier)
                                }
                                navigationUrl={this.props.dataProvider.buildTabNavigationUrl(
                                    tab.associatedData.seoIdentifier
                                )}
                            >
                                {tab.name}
                            </Tab>
                        ))}
                    </TabsWrapper>
                    {this.props.dataProvider.tabs.map((tab) => (
                        <Content route={tab.id} key={tab.id} controller={this.props.dataProvider.tabSwitcherController}>
                            <SubTabsWrapper dictionary={this.props.localizationConfig?.tabSwitcher}>
                                {tab.subTabs.map((subtab) => (
                                    <SubTab
                                        route={`${tab.id}/${subtab.id}`}
                                        key={subtab.id}
                                        controller={this.props.dataProvider.tabSwitcherController}
                                        isSelectedByDefault={tab.isSelected && subtab.isSelected}
                                        onClick={() => this.props.dataProvider.onSubTabClick(subtab.id)}
                                        navigationUrl=""
                                        controlsId={`tabpanel-${tab.id}`}
                                    >
                                        <div>{subtab.name}</div>
                                    </SubTab>
                                ))}
                            </SubTabsWrapper>
                        </Content>
                    ))}
                    {this.props.dataProvider.tabId &&
                        this.props.dataProvider.renderPromoContainerWrapper(this.props.dataProvider.tabId)}
                    {this.props.dataProvider.isError && (
                        <LivePageError
                            title={this.localizationConfig?.livePage.onLoadErrorTitle || dict.onLoadErrorTitle}
                            text={this.localizationConfig?.livePage.onLoadErrorText || dict.onLoadErrorText}
                            buttonText={this.localizationConfig?.livePage.refreshButtonText || dict.refreshButtonText}
                            onClick={this.props.dataProvider.onRefreshButtonClick}
                        />
                    )}
                    {!this.props.dataProvider.isError && (
                        <div className="lp-live-page-widget__sections">
                            {this.props.dataProvider.showOverlay && (
                                <div
                                    className="lp-live-page-widget__overlay"
                                    data-testid="lp-live-page-widget-overlay"
                                />
                            )}
                            {this.props.dataProvider.sections.map((section, sectionIndex) => {
                                const { seoIdentifier } = section.associatedData;
                                const isFirstSection = sectionIndex === 0;
                                return (
                                    <Section
                                        sectionRef={isFirstSection ? this.marketboardRef : undefined}
                                        key={section.id}
                                        title={section.name}
                                        seoIdentifier={seoIdentifier}
                                        isExpanded={section.isExpanded}
                                        onClick={() =>
                                            this.props.dataProvider.onSectionClick(section.id, section.isExpanded)
                                        }
                                        isSectionLoading={this.props.dataProvider.loadingSectionIds.includes(
                                            section.id
                                        )}
                                        renderLoadingCard={this.props.dataProvider.renderLoadingCardWrapper}
                                        renderLeagueNavLink={this.props.dataProvider.renderLeaguePageNavLink}
                                        subTabComponentId={this.props.dataProvider.subTabComponentId}
                                        toggleSectionAriaLabel={
                                            this.props.localizationConfig?.livePage?.toggleSectionAriaLabel ||
                                            dict.toggleSectionAriaLabel
                                        }
                                        animationConfig={{
                                            duration: 300,
                                            ease: 'ease-in-out'
                                        }}
                                    >
                                        {Array.from(
                                            this.props.dataProvider.groupedEventsBySection.get(section.id)?.values() ||
                                                []
                                        )
                                            .flatMap((eventsMap) => {
                                                const filteredEvents = eventsMap.filter(
                                                    (event) => event.status !== 'FINISHED'
                                                );
                                                return filteredEvents.length > 0 ? [filteredEvents] : [];
                                            })
                                            .map((eventsMap) => (
                                                <Marketboard
                                                    key={eventsMap[0].id}
                                                    section={section}
                                                    eventsMap={eventsMap}
                                                    consumerVersion={this.props.consumerVersion}
                                                    productConfig={this.props.dataProvider.productConfig}
                                                    marketboardRef={this.marketboardRef}
                                                    trackEvent={this.props.trackEvent}
                                                    playerPageConfig={this.props.dataProvider.playerPageConfig}
                                                    getPlayerLink={this.props.dataProvider.getPlayerLink}
                                                    onPlayerLinkClick={this.props.dataProvider.onPlayerLinkClick}
                                                    getTeamLogo={this.props.getTeamLogo}
                                                    onToggleExpandClick={this.props.dataProvider.onToggleExpandClick}
                                                    subTabComponentId={this.props.dataProvider.subTabComponentId}
                                                    localizationConfig={this.props.localizationConfig}
                                                    locale={this.props.locale}
                                                    isSingleSectionWithOneGroup={
                                                        this.props.dataProvider.sections.length === 1 &&
                                                        this.props.dataProvider.groupedEventsBySection.get(section.id)
                                                            ?.size === 1
                                                    }
                                                />
                                            ))}
                                    </Section>
                                );
                            })}
                        </div>
                    )}
                </section>
            </>
        );
    }
}

export const App = observer(AppComponent);
